import * as React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { BadgeCheck, Building2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import IdentityProviderButtons from '@/components/auth/IdentityProviderButtons';
import { signup } from '@/data/billing';
import WizardSteps from '@/components/onboarding/WizardSteps';
import { useAuth } from '@/context/AuthContext';

const schema = z
  .object({
    first_name: z.string().max(255).optional(),
    last_name: z.string().max(255).optional(),
    email: z.string().email('Enter a valid email address'),
    password: z.string().min(8, 'At least 8 characters'),
    confirm_password: z.string().min(1, 'Confirm your password'),
  })
  .refine((data) => data.password === data.confirm_password, {
    path: ['confirm_password'],
    message: 'Passwords do not match',
  });

type Values = z.infer<typeof schema>;

type SignupFailure = { message: string; accountExists: boolean };

function signupError(error: unknown): SignupFailure {
  if (error instanceof AxiosError) {
    const data = error.response?.data as
      | { error?: string; detail?: string; code?: string; password?: string[]; email?: string[] }
      | undefined;

    if (data?.code === 'account_exists') {
      return { message: data.error ?? 'That email already has an account.', accountExists: true };
    }
    if (data?.error) return { message: data.error, accountExists: false };
    if (data?.detail) return { message: data.detail, accountExists: false };
    if (data?.password?.length) return { message: data.password.join(' '), accountExists: false };
    if (data?.email?.length) return { message: data.email.join(' '), accountExists: false };
  }
  return { message: 'Could not create your account. Please try again.', accountExists: false };
}

export default function SignupPage() {
  const navigate = useNavigate();
  const { signInWithToken } = useAuth();
  const [params] = useSearchParams();
  const [submitting, setSubmitting] = React.useState<'customer' | 'advisor' | null>(null);
  const [failure, setFailure] = React.useState<SignupFailure | null>(null);

  const advisorId = params.get('advisor');

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      confirm_password: '',
    },
  });

  const onSubmit = async (values: Values, joining: 'customer' | 'advisor') => {
    setSubmitting(joining);
    setFailure(null);
    try {
      const result = await signup({
        email: values.email,
        password: values.password,
        first_name: values.first_name,
        last_name: values.last_name,
      });
      await signInWithToken(result.access_token);
      if (!result.verification_email_sent) {
        toast.warning(
          'We could not send your verification email. You can still set up your organisation.',
        );
      }
      const next = joining === 'advisor'
        ? '/advisor-onboarding'
        : advisorId
          ? `/onboarding?advisor=${advisorId}`
          : '/onboarding';
      navigate(next, { replace: true });
    } catch (error) {
      const problem = signupError(error);
      setFailure(problem);
      toast.error(problem.message);
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div className="min-h-screen w-full bg-background flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-3xl space-y-4" data-testid="signup-page">
        <WizardSteps current={0} />

        <Card>
          <CardHeader>
            <CardTitle>Create your BitSwan account</CardTitle>
            <CardDescription>
              Your details first. What you are signing up as is the button you
              press at the end; you choose your plan a couple of steps later.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-5">
              <IdentityProviderButtons
                label="or with an email and password"
                heading="Sign up with"
              />
            </div>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <FormField
                    control={form.control}
                    name="first_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First name</FormLabel>
                        <FormControl>
                          <Input {...field} data-testid="signup-first-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="last_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last name</FormLabel>
                        <FormControl>
                          <Input {...field} data-testid="signup-last-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Work email</FormLabel>
                      <FormControl>
                        <Input type="email" {...field} data-testid="signup-email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input type="password" {...field} data-testid="signup-password" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirm_password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          {...field}
                          data-testid="signup-confirm-password"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {failure && (
                  <div
                    className="rounded-md border border-red-500/40 bg-red-500/5 p-3 text-sm"
                    role="alert"
                    data-testid="signup-error"
                  >
                    <p className="text-red-700">{failure.message}</p>
                    {failure.accountExists && (
                      <p className="mt-1">
                        <Link
                          to={`/login`}
                          className="underline"
                          data-testid="signup-error-signin"
                        >
                          Sign in instead
                        </Link>
                      </p>
                    )}
                  </div>
                )}

                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Button
                      type="button"
                      className="w-full"
                      disabled={submitting}
                      onClick={form.handleSubmit((values) => onSubmit(values, 'customer'))}
                      data-testid="signup-submit"
                    >
                      {submitting === 'customer' && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      <Building2 className="mr-2 h-4 w-4" />
                      Create organisation
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      Sets up a new organisation for your company, with a
                      30-day trial. If your company already uses BitSwan, ask
                      an admin there to invite you instead, so you join the one
                      your colleagues are already working in.
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      disabled={submitting}
                      onClick={form.handleSubmit((values) => onSubmit(values, 'advisor'))}
                      data-testid="signup-submit-advisor"
                    >
                      {submitting === 'advisor' && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      <BadgeCheck className="mr-2 h-4 w-4" />
                      Become an advisor today
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      For consultants who set automations up for other
                      companies. You are in straight away; your profile is
                      listed publicly once BitSwan has certified you.
                    </p>
                  </div>
                </div>
              </form>
            </Form>

            <p className="text-sm text-muted-foreground mt-4 text-center">
              Already have an account?{' '}
              <Link to="/login" className="underline" data-testid="signup-login-link">
                Sign in
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
